import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Alert,
  CircularProgress,
} from '@mui/material';
import { AdminService } from '../../services/adminService';

function tripletKey(triplet) {
  if (!triplet?.domain) return '';
  return `${triplet.domain}-${triplet.competence}-${triplet.notion}`;
}

function formatTripletLabel(triplet) {
  if (!triplet) return '—';
  const parts = [
    triplet.domain || '—',
    triplet.competence || '—',
    triplet.notion || '—',
  ];
  return parts.join(' / ');
}

export default function TaxonomyReassignModal({
  open,
  onClose,
  sourceTriplet,
  exerciseCount = 0,
  validTriplets = [],
  onSuccess,
}) {
  const [targetKey, setTargetKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const tripletsByDomain = useMemo(() => {
    const map = {};
    validTriplets.forEach((triplet) => {
      if (!map[triplet.domain]) map[triplet.domain] = [];
      map[triplet.domain].push(triplet);
    });
    return map;
  }, [validTriplets]);

  const handleClose = () => {
    if (submitting) return;
    setTargetKey('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    const found = validTriplets.find((t) => tripletKey(t) === targetKey);
    if (!found || !sourceTriplet) {
      setError('Sélectionnez un triplet cible valide');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await AdminService.reassignExerciseTaxonomy({
        source: sourceTriplet,
        target: {
          domain: found.domain,
          competence: found.competence,
          notion: found.notion,
        },
      });
      onSuccess?.(result);
      setTargetKey('');
      onClose();
    } catch (err) {
      console.error('Erreur réassignation taxonomie:', err);
      setError(err?.message || err?.response?.data?.error || 'Échec de la réassignation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Réassigner la taxonomie</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {exerciseCount} exercice{exerciseCount > 1 ? 's' : ''} seront mis à jour.
          Les exercices validés repasseront en « en attente ».
        </Typography>

        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Triplet actuel (orphelin)
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {formatTripletLabel(sourceTriplet)}
          </Typography>
        </Box>

        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>Triplet cible (référentiel serveur)</InputLabel>
          <Select
            value={targetKey}
            label="Triplet cible (référentiel serveur)"
            onChange={(e) => setTargetKey(e.target.value)}
            disabled={submitting || validTriplets.length === 0}
          >
            <MenuItem value="">
              <em>Sélectionner…</em>
            </MenuItem>
            {Object.entries(tripletsByDomain).map(([domain, triplets]) => [
              <ListSubheader key={`header-${domain}`}>
                {triplets[0]?.domainLabel || domain}
              </ListSubheader>,
              ...triplets.map((triplet) => {
                const key = tripletKey(triplet);
                const label = `${triplet.competenceLabel} — ${triplet.notionLabel}`;
                return (
                  <MenuItem key={key} value={key}>
                    <Box>
                      <Typography variant="body2">{label}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {triplet.domain} / {triplet.competence} / {triplet.notion}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              }),
            ])}
          </Select>
        </FormControl>

        {validTriplets.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Référentiel indisponible — rechargez la page.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !targetKey}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Réassigner
        </Button>
      </DialogActions>
    </Dialog>
  );
}
